export type Action =
  | 'skill0'
  | 'skill1'
  | 'skill2'
  | 'skill3'
  | 'item1'
  | 'item2'
  | 'dodge'
  | 'interact'
  | 'target_next'
  | 'escape'
  | 'inventory'
  | 'character'
  | 'quests'
  | 'map'
  | 'chat'
  | 'help'
  | 'cam_left'
  | 'cam_right';

/** Default key bindings (KeyboardEvent.code). */
export const KEYBINDS: Record<string, Action> = {
  Digit1: 'skill0',
  Digit2: 'skill1',
  Digit3: 'skill2',
  Digit4: 'skill3',
  Digit5: 'item1',
  Digit6: 'item2',
  Space: 'dodge',
  KeyF: 'interact',
  Tab: 'target_next',
  Escape: 'escape',
  KeyI: 'inventory',
  KeyB: 'inventory',
  KeyC: 'character',
  KeyL: 'quests',
  KeyJ: 'quests',
  KeyM: 'map',
  Enter: 'chat',
  KeyH: 'help',
  KeyQ: 'cam_left',
  KeyE: 'cam_right',
};

/**
 * Keyboard / mouse / touch input. Movement keys are sampled continuously; discrete actions are
 * queued and consumed by the game loop. Keys are ignored while typing in text fields.
 */
export class Input {
  private readonly down = new Set<string>();
  private readonly queue: Action[] = [];
  private readonly held = new Set<Action>();
  mouseX = 0;
  mouseY = 0;
  mouseInside = false;
  primaryHeld = false;
  /** Virtual joystick from touch controls (-1..1). */
  touchMove = { x: 0, y: 0 };
  /** Accumulated wheel delta since last read. */
  wheel = 0;
  /** Accumulated right-drag yaw (pixels). */
  dragYaw = 0;
  private rightDragging = false;
  private lastDragX = 0;
  onClick: ((x: number, y: number) => void) | null = null;
  private readonly cleanup: (() => void)[] = [];

  attach(canvas: HTMLCanvasElement): void {
    const isTyping = (e: Event): boolean => {
      const t = e.target as HTMLElement | null;
      return !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
    };
    const kd = (e: KeyboardEvent): void => {
      if (isTyping(e)) return;
      const action = KEYBINDS[e.code];
      if (action && (action === 'target_next' || action === 'dodge' || action === 'escape'))
        e.preventDefault();
      if (!e.repeat && action) this.queue.push(action);
      if (action) this.held.add(action);
      this.down.add(e.code);
    };
    const ku = (e: KeyboardEvent): void => {
      this.down.delete(e.code);
      const action = KEYBINDS[e.code];
      if (action) this.held.delete(action);
    };
    const blur = (): void => {
      this.down.clear();
      this.held.clear();
      this.primaryHeld = false;
      this.rightDragging = false;
    };
    const pm = (e: PointerEvent): void => {
      const r = canvas.getBoundingClientRect();
      this.mouseX = e.clientX - r.left;
      this.mouseY = e.clientY - r.top;
      this.mouseInside = true;
      if (this.rightDragging) {
        this.dragYaw += e.clientX - this.lastDragX;
        this.lastDragX = e.clientX;
      }
    };
    const pd = (e: PointerEvent): void => {
      if (e.pointerType === 'touch') return;
      const r = canvas.getBoundingClientRect();
      this.mouseX = e.clientX - r.left;
      this.mouseY = e.clientY - r.top;
      if (e.button === 0) {
        this.primaryHeld = true;
        this.onClick?.(this.mouseX, this.mouseY);
      } else if (e.button === 2) {
        this.rightDragging = true;
        this.lastDragX = e.clientX;
      }
    };
    const pu = (e: PointerEvent): void => {
      if (e.button === 0) this.primaryHeld = false;
      if (e.button === 2) this.rightDragging = false;
    };
    const wheel = (e: WheelEvent): void => {
      e.preventDefault();
      this.wheel += e.deltaY;
    };
    const ctx = (e: Event): void => e.preventDefault();
    const leave = (): void => {
      this.mouseInside = false;
    };
    window.addEventListener('keydown', kd);
    window.addEventListener('keyup', ku);
    window.addEventListener('blur', blur);
    window.addEventListener('pointermove', pm);
    window.addEventListener('pointerup', pu);
    canvas.addEventListener('pointerdown', pd);
    canvas.addEventListener('wheel', wheel, { passive: false });
    canvas.addEventListener('contextmenu', ctx);
    canvas.addEventListener('pointerleave', leave);
    this.cleanup.push(() => {
      window.removeEventListener('keydown', kd);
      window.removeEventListener('keyup', ku);
      window.removeEventListener('blur', blur);
      window.removeEventListener('pointermove', pm);
      window.removeEventListener('pointerup', pu);
      canvas.removeEventListener('pointerdown', pd);
      canvas.removeEventListener('wheel', wheel);
      canvas.removeEventListener('contextmenu', ctx);
      canvas.removeEventListener('pointerleave', leave);
    });
  }

  detach(): void {
    for (const c of this.cleanup) c();
    this.cleanup.length = 0;
  }

  /** Raw movement intent in screen space: x right, y forward (up on screen). */
  moveAxis(): { x: number; y: number } {
    let x = 0;
    let y = 0;
    if (this.down.has('KeyW') || this.down.has('ArrowUp')) y += 1;
    if (this.down.has('KeyS') || this.down.has('ArrowDown')) y -= 1;
    if (this.down.has('KeyD') || this.down.has('ArrowRight')) x += 1;
    if (this.down.has('KeyA') || this.down.has('ArrowLeft')) x -= 1;
    if (x === 0 && y === 0) return { x: this.touchMove.x, y: this.touchMove.y };
    const l = Math.hypot(x, y);
    return { x: x / l, y: y / l };
  }

  sprint(): boolean {
    return (
      this.down.has('ShiftLeft') ||
      this.down.has('ShiftRight') ||
      Math.hypot(this.touchMove.x, this.touchMove.y) > 0.95
    );
  }

  isHeld(a: Action): boolean {
    return this.held.has(a);
  }

  /** Programmatic trigger (touch buttons, HUD hotbar clicks). */
  trigger(a: Action): void {
    this.queue.push(a);
  }

  drain(): Action[] {
    const out = this.queue.splice(0, this.queue.length);
    return out;
  }

  takeWheel(): number {
    const w = this.wheel;
    this.wheel = 0;
    return w;
  }

  takeDragYaw(): number {
    const d = this.dragYaw;
    this.dragYaw = 0;
    return d;
  }
}
